import { MultiSelectOptionValue } from '@collector/shared-ui'
import { MaybeGetter } from '@collector/shared-utils'
import { Incident } from '@collector/sportsapi-client'
import {
  IncidentAttributeId,
  IncidentId,
} from '@collector/sportsapi-client-legacy'

import { IncidentPlayerOption } from '../../types'

export type ShouldLoadOppositeSubParticipants =
  boolean | ((attributeIds: IncidentAttributeId[]) => boolean)

export type FilterSubParticipantsOptions = (
  subParticipants: IncidentPlayerOption[],
) => IncidentPlayerOption[]

export type SubParticipantSettings = {
  name: string
  required?: boolean
  keepSelectedOption?: boolean
  shouldLoadOppositeSubParticipants?: ShouldLoadOppositeSubParticipants
  filterSubParticipantsOptions?: FilterSubParticipantsOptions
  isAvailable?: (incident: Incident) => boolean
}

export type IncidentSettings = {
  participant?: string | SubParticipantSettings
  assistant?: MaybeGetter<string | SubParticipantSettings, Incident>
  secondary_assistant?: SubParticipantSettings
  attributeIds?: MaybeGetter<IncidentAttributeId[] | undefined, Incident>
  singleAttributes?: boolean
  /** label for display_info property of Incident */
  displayInfo?: string
  swappable?: MaybeGetter<boolean, Incident>
  attributesSelectionConstraint?: (
    newSelection: MultiSelectOptionValue[],
    previousSelection: MultiSelectOptionValue[],
  ) => MultiSelectOptionValue[]
}

export type HistoryIncidentsConfig<T extends IncidentId = IncidentId> = {
  [key in T]?: IncidentSettings
}

// TODO: move to upper scope
export type SubParticipantType =
  'participant' | 'assistant' | 'secondary_assistant'

type SubParticipantProperties<
  IdKey extends keyof Incident,
  NameKey extends keyof Incident,
> = {
  propertyId: IdKey
  propertyName: NameKey
}

type ParticipantProperties = SubParticipantProperties<
  'participant_id',
  'participant_name'
>

type AssistantProperties = SubParticipantProperties<
  'assistant_id',
  'assistant_name'
>

// TODO: better type using incident path
// additional_info.secondary_assistant_id
// additional_info.secondary_assistant_name
export type AdditionalInfoSecondaryAssistantProperties = {
  propertyId: 'secondary_assistant_id'
  propertyName: 'secondary_assistant_name'
}

export type IncidentSubParticipantProperties =
  ParticipantProperties | AssistantProperties

export function getSubparticipantProperties<T extends SubParticipantType>(
  type: T,
):
  | IncidentSubParticipantProperties
  | AdditionalInfoSecondaryAssistantProperties {
  switch (type) {
    case 'participant':
      return {
        propertyId: 'participant_id',
        propertyName: 'participant_name',
      }
    case 'assistant':
      return {
        propertyId: 'assistant_id',
        propertyName: 'assistant_name',
      }
    case 'secondary_assistant':
      return {
        propertyId: 'secondary_assistant_id',
        propertyName: 'secondary_assistant_name',
      }
    default:
      throw new Error(`Unsupported subparticipant type: ${type}`)
  }
}

export function isSubParticipantSetting(
  value: string | SubParticipantSettings | undefined,
): value is SubParticipantSettings {
  return typeof value === 'object'
}
